import type { PlanCode } from "./apiContracts";
import type { PrototypeApiClient } from "./apiClient";
import type { Root } from "./prototypeTypes";
import { mountedFor, refreshMediaSessionPlayer, toggleMediaPlayer } from './mediaPlayerCore';
import { getChapterCount, navigateToVideoChapter, stopVideoChapterTransitions } from "./mediaPlayerChapterPlayback";
import {
  moveVideo,
  stopVideoControls,
  wireVideoCommands,
  wireVideoProgress,
} from "./mediaPlayerVideoControls";
import { wireVideoGestureController } from './videoGestureController';
import { stopVideoWatermarksIn } from "./videoWatermark";
import { playerSelectors } from "./mediaPlayerSelectors";
import { runtimeOf } from "./mediaPlayerProgress";
import { syncVideoPresentationControls } from './videoPresentationControls';
import { applyVideoBrowserChrome, restoreVideoBrowserChrome } from './videoBrowserChrome';
import { closeVideoPresentationSurface } from './videoPresentationSurface';
import { getTopic } from './topicStore';
import { matchingPageModelTopic } from './nowPlayingPlaylistController';

let vfGestureCleanup: (() => void) | null = null;
let vfWheelAbort: AbortController | null = null;
let vfControlAbort: AbortController | null = null;
let vfKeyboardCleanup: (() => void) | null = null;

interface VideoChapterDot {
  index: number;
  title: string;
  duration: string;
  hasAudio?: boolean;
  hasVideo?: boolean;
}

export function attachVideoPlayer(
  root: Root,
  api: PrototypeApiClient,
  topicId: string,
  chapterIndex: number,
  plan: PlanCode,
): void {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	const surface = doc.getElementById(playerSelectors.video.container);
	if (surface?.classList.contains('active')) applyVideoBrowserChrome(doc);
	else restoreVideoBrowserChrome(doc);
  // Controls render hidden in the static template. Reveal only the actions
  // this browser can actually perform before the active overlay can paint.
  const mountedVideo = mountedFor(root, 'video')?.element;
  syncVideoPresentationControls(root, mountedVideo instanceof HTMLVideoElement ? mountedVideo : undefined);
  doc.getElementById(playerSelectors.video.replay)?.classList.remove("active");
  doc.getElementById(playerSelectors.video.container)?.classList.remove("replay-active");
  // Defensive teardown: any prior gesture/progress/wheel listeners from a
  // previous open path must be released before wiring a fresh set.
  // `attachVideoPlayer` is also called on chapter transitions where the
  // surface is already visible; without this reset, two pointer listeners
  // would compete and the tap-to-toggle behaviour would intermittently
  // stall (a stale `vfGestureCleanup` would intercept the first tap).
  // This must NOT use stopVideoPlayerEnhancements: that also removes the
  // `active` class (closing the overlay), so a fresh open that already
  // added `active` would end up hidden while the video keeps playing.
  releaseVideoPlayerEnhancements(root);
  wireVideoGestures(root, api, topicId, chapterIndex, plan);
  wireVideoProgress(root);
  wireVideoCommands(root, api, topicId, chapterIndex, plan);
	wireVideoPlaybackKeyboard(root);
  void paintVideoChrome(root, api, topicId, chapterIndex, plan);
	refreshMediaSessionPlayer(root, 'video');
}

export function releaseVideoPlayerEnhancements(root?: Root): void {
  vfGestureCleanup?.();
  vfWheelAbort?.abort();
  vfControlAbort?.abort();
	vfKeyboardCleanup?.();
  stopVideoControls();
  vfGestureCleanup = null;
  vfWheelAbort = null;
  vfControlAbort = null;
	vfKeyboardCleanup = null;
  stopVideoChapterTransitions(root);
  stopVideoWatermarksIn(`#${playerSelectors.video.container}`);
}

function wireVideoPlaybackKeyboard(root: Root): void {
	const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	vfKeyboardCleanup?.();
	const onKeydown = (event: KeyboardEvent) => {
		if (event.key !== ' ' && event.key !== 'Spacebar') return;
		const surface = doc.getElementById(playerSelectors.video.container);
		if (!surface?.classList.contains('active')) return;
		const target = event.target as HTMLElement | null;
		if (target?.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
		// A focused control must not convert Space into seek/chapter/back action.
		// Tab still works normally; only Space is owned by active video playback.
		event.preventDefault();
		event.stopImmediatePropagation();
		if (event.repeat) return;
		toggleMediaPlayer(root, 'video');
	};
	doc.addEventListener('keydown', onKeydown, true);
	vfKeyboardCleanup = () => doc.removeEventListener('keydown', onKeydown, true);
}

export function stopVideoPlayerEnhancements(root?: Root): void {
  releaseVideoPlayerEnhancements(root);
  // Closing the fullscreen overlay is part of the teardown: without this, a
  // view change that unmounts the video (e.g. a browser Back that re-applies
  // an article route) left #videoFullscreen.active in place — a black screen
  // with only the control panel visible.
	if (root) closeVideoPresentationSurface(root);
	else closeVideoPresentationSurface(document);
}

function wireVideoGestures(root: Root, api: PrototypeApiClient, topicId: string, chapterIndex: number, plan: PlanCode): void {
  const vf = (root as unknown as Document).getElementById?.(playerSelectors.video.container)
    ?? document.getElementById(playerSelectors.video.container);
  if (!vf) return;
  // Read the current chapter from the runtime on each gesture so the
  // coordinator's move() always starts from the actually-mounted chapter.
  // The closure chapterIndex goes stale after a chapter transition because
  // attachVideoPlayer isn't re-invoked synchronously -- the article API
  // call that re-wires media is async. A stale chapterIndex caused
  // syncMounted() to reset the coordinator to the old chapter, and the
  // next move(1) advanced desiredChapter by 1 from the stale value,
  // producing a double-jump (e.g. ch1 -> ch3 in one swipe).
  const currentChapter = () => {
    const rt = runtimeOf(root);
    return Number.isFinite(rt?.activeChapterIndex) && (rt?.activeChapterIndex ?? 0) > 0 ? rt!.activeChapterIndex! : chapterIndex;
  };
  const currentTopic = () => {
    const rt = runtimeOf(root);
    return rt?.activeCourseId || topicId;
  };
  vfGestureCleanup?.();
  vfGestureCleanup = wireVideoGestureController(vf, {
    onNext: () => moveVideo(root, api, currentTopic(), currentChapter(), plan, 1),
    onPrevious: () => moveVideo(root, api, currentTopic(), currentChapter(), plan, -1),
  });
  vfWheelAbort?.abort();
  vfWheelAbort = typeof AbortController !== "undefined" ? new AbortController() : null;
  const eventOptions = vfWheelAbort
    ? ({ passive: true, signal: vfWheelAbort.signal } as AddEventListenerOptions)
    : ({ passive: true } as AddEventListenerOptions);

  // Trackpad two-finger swipe emits a high-density stream of wheel events
  // that can last 500-900ms (including momentum inertia after the fingers
  // lift). The coordinator's move() ACCUMULATES -- two moveVideo() calls
  // before the async commit settles jump 2 chapters. So the wheel handler
  // must guarantee moveVideo is called at most once per physical gesture.
  //
  // Three defenses, layered:
  //
  // 1. ev.timeStamp (DOMHighResTimeStamp) instead of Date.now().
  //    timeStamp records when the browser *generated* the event, immune to
  //    main-thread stalls. The commit callback does synchronous DOM work
  //    (showArticle, showPlayer, playMediaPlayer) that can block the main
  //    thread >120ms. With Date.now() that delay inflates the inter-event
  //    gap and falsely signals "momentum ended."
  //
  // 2. Hard minimum cooldown (MIN_COOLDOWN_MS). After nav, unconditionally
  //    suppress all events for 400ms regardless of gaps. This bounds the
  //    worst case: two swipes within 400ms merge into one nav (safe) vs
  //    momentum tail jumping a chapter (bug). 400ms covers most trackpad
  //    momentum durations.
  //
  // 3. Gap-based momentum tail absorption. After the cooldown, require a
  //    real timeStamp gap (GAP_MS) before accumulation resumes. This
  //    catches momentum tails longer than 400ms without blocking
  //    deliberate second swipes (which have a natural pause).
  const MIN_COOLDOWN_MS = 400;
  const GAP_MS = 120;
  let wheelAccum = 0;
  let navAt = 0;
  let prevEventTs = 0;
  let prevDir = 0;
  vf.addEventListener("wheel", (ev) => {
    const ts = Number.isFinite(ev.timeStamp) && ev.timeStamp > 0 ? ev.timeStamp : Date.now();
    // Defense 2: hard cooldown -- suppress everything for MIN_COOLDOWN_MS
    // after navigation, no exceptions. This absorbs the bulk of trackpad
    // momentum (typically 300-900ms) regardless of main-thread timing.
    if (navAt > 0 && ts - navAt < MIN_COOLDOWN_MS) {
      wheelAccum = 0;
      prevEventTs = ts;
      return;
    }
    // Defense 3: after the hard cooldown, require a real gap in the
    // event stream before accumulation resumes. If events are still
    // arriving in rapid succession (long momentum tail), keep swallowing.
    if (navAt > 0) {
      const sinceLast = ts - prevEventTs;
      if (sinceLast < GAP_MS) {
        wheelAccum = 0;
        prevEventTs = ts;
        return;
      }
      // Gap detected -- momentum ended. Start fresh accumulation.
      navAt = 0;
      wheelAccum = 0;
    }
    // Reset accumulator on direction reversal so oscillating input
    // (user changes scroll direction mid-gesture) doesn't fire nav
    // in the wrong direction.
    const dir = ev.deltaY > 0 ? 1 : ev.deltaY < 0 ? -1 : 0;
    if (dir !== 0 && prevDir !== 0 && dir !== prevDir) {
      wheelAccum = 0;
    }
    if (dir !== 0) prevDir = dir;
    wheelAccum += ev.deltaY;
    prevEventTs = ts;
    if (Math.abs(wheelAccum) < 200) return;
    const navDir = wheelAccum > 0 ? 1 : -1;
    wheelAccum = 0;
    navAt = ts;
    moveVideo(root, api, currentTopic(), currentChapter(), plan, navDir);
  }, eventOptions);
}

async function paintVideoChrome(root: Root, api: PrototypeApiClient, topicId: string, chapterIndex: number, plan: PlanCode): Promise<void> {
  const active = (root as unknown as {
		__activeChapter?: { index?: number; title?: string; topicTitle?: string; topicSlug?: string };
  }).__activeChapter;
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const titleNode = doc.getElementById(playerSelectors.video.chapterTitle);
  const labelNode = doc.getElementById(playerSelectors.video.chapterLabel);
  const topicNode = doc.getElementById(playerSelectors.video.topicName);
  const descNode = doc.getElementById(playerSelectors.video.chapterDesc);
	const title = active?.title?.trim() || `Chapter ${chapterIndex}`;
	// A page transition briefly clears/replaces __activeChapter. Resolve the
	// chrome from the mounted video's topic identity first and never project an
	// unrelated page's Topic name (or an empty transitional value) into video.
	const activeMatchesVideo = active?.topicSlug === topicId;
	const indexedTopicTitle = getTopic(topicId)?.title?.trim();
	const pageModelTopicTitle = matchingPageModelTopic(root, topicId)?.title?.trim();
	const topicTitle = (activeMatchesVideo ? active?.topicTitle?.trim() : undefined) || indexedTopicTitle || pageModelTopicTitle || 'DeepMox';
  if (titleNode) titleNode.textContent = title;
  if (topicNode) topicNode.textContent = topicTitle;
  if (labelNode) labelNode.textContent = `Chapter ${chapterIndex}`;
  if (descNode) descNode.textContent = "Loading chapter timeline...";

  let chapters: VideoChapterDot[] = [];
  try {
    const response = await api.listTopicChapters(topicId);
    chapters = Array.isArray(response.chapters) ? response.chapters : [];
  } catch {
    const total = await getChapterCount(api, topicId);
    chapters = total > 0
      ? Array.from({ length: total }, (_, idx) => ({ index: idx + 1, title: `Chapter ${idx + 1}`, duration: "" }))
      : [{ index: chapterIndex, title, duration: "" }];
  }
  const current = chapters.find((chapter) => chapter.index === chapterIndex)
    ?? { index: chapterIndex, title, duration: "", hasAudio: true, hasVideo: true };
  const total = Math.max(chapters.length, current.index);
  (root as unknown as { __videoChapterCount?: number }).__videoChapterCount = total;
  if (titleNode) titleNode.textContent = current.title || title;
  if (labelNode) {
    const details = [
      `Chapter ${current.index} of ${total}`,
      formatChapterDuration(current.duration),
    ].filter(Boolean);
    labelNode.textContent = details.join(" · ");
  }
  if (descNode) {
    descNode.textContent = topicTitle;
  }
  renderChapterDots(root, chapters.length > 0 ? chapters : [current], chapterIndex, topicTitle, plan, api, topicId);
}

function formatChapterDuration(value: string | undefined): string {
  const text = (value ?? "").trim();
  if (!text) return "";
  if (/\bmin\b/i.test(text)) return text;
  const number = text.match(/\d+(?:\.\d+)?/)?.[0];
  return number ? `${number} min` : text;
}

function renderChapterDots(
  root: Root,
  chapters: VideoChapterDot[],
  activeIndex: number,
  topicTitle: string,
  plan: PlanCode,
  api: PrototypeApiClient,
  topicId: string,
): void {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const dots = doc.getElementById(playerSelectors.video.chapterDots);
  if (!dots) return;
  vfControlAbort?.abort();
  vfControlAbort = typeof AbortController !== "undefined" ? new AbortController() : null;
  const eventOptions = vfControlAbort ? ({ signal: vfControlAbort.signal } as AddEventListenerOptions) : undefined;
  dots.replaceChildren(...chapters.map((chapter) => {
    const dot = document.createElement("button");
    dot.type = "button";
    dot.className = `vf-dot${chapter.index === activeIndex ? " active" : ""}${chapter.index < activeIndex ? " played" : ""}`;
    dot.setAttribute("aria-label", `${chapter.title}, ${topicTitle}`);
    dot.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (chapter.index !== activeIndex) navigateToVideoChapter(root, topicId, chapter.index, plan, api);
    }, eventOptions);
    const tooltip = document.createElement("span");
    tooltip.className = "vf-dot-tooltip";
    const main = document.createElement("strong");
    main.textContent = chapter.title;
    const sub = document.createElement("span");
    sub.textContent = [topicTitle, chapter.duration].filter(Boolean).join(" · ");
    tooltip.appendChild(main);
    tooltip.appendChild(sub);
    dot.appendChild(tooltip);
    return dot;
  }));
}
